import { Button, Grid, Stack, Typography } from '@mui/material'
import { useEffect, useRef } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { AppleTripError, appleTripFinish } from 'src/api/appleTrip'
import { consoleSessionHeld } from 'src/api/consoleSessionHeld'
import AnimatedLogo from 'src/components/AnimatedLogo'
import Page from 'src/components/layout/Page'
import { useLogin } from 'src/pages/LoginPage/parts/useLogin'

// ----------------------------------------------------------------------
// Where the browser lands after Apple.
//
// Apple posts its answer to the console's own origin, where the API keeps it
// for the trip this browser holds and sends the browser here with the trip's
// state, or with the error Apple named. The
// page asks the backend to finish the trip this browser holds, and does what
// the answer says: a sign-in signs in, a sign-in that owes the account's
// authenticator code asks for it on the login page, and a link is reported in
// Settings, where it started. A failure is said where the person can act on
// it: in Settings while somebody is signed in, on the login page otherwise.
//
// An address with neither a state nor an error was typed or bookmarked, and
// there is nothing to finish: the page says so where it stands rather than
// sending anybody anywhere.
// ----------------------------------------------------------------------

export const ACCOUNT_SETTINGS_PATH = '/admin/account/settings'

const DID_NOT_FINISH = 'Apple did not complete the sign in. Try again.'

/** Apple's code for the person closing its page rather than signing in. */
const CANCELLED = 'user_cancelled_authorize'

const AppleOauthPage = () => {
  const { teslaSignInComplete } = useLogin()
  const navigate = useNavigate()
  const { search } = useLocation()
  const processedRef = useRef(false)
  const params = new URLSearchParams(search)
  const nothingToFinish = !params.get('state') && !params.get('error')

  useEffect(() => {
    // Once, including under strict mode's second effect.
    if (processedRef.current || nothingToFinish) {
      return
    }
    processedRef.current = true

    const returned = new URLSearchParams(search)
    const state = returned.get('state')
    const declined = returned.get('error')

    const refuse = async (message: string) => {
      if ((await consoleSessionHeld()) === true) {
        navigate(ACCOUNT_SETTINGS_PATH, {
          replace: true,
          state: { appleError: message },
        })
      } else {
        navigate('/login', { replace: true, state: { error: message } })
      }
    }

    void (async () => {
      if (declined || !state) {
        await refuse(
          declined === CANCELLED
            ? 'Apple sign in was cancelled.'
            : DID_NOT_FINISH,
        )
        return
      }

      try {
        const outcome = await appleTripFinish(state)

        if (outcome.purpose === 'link') {
          navigate(ACCOUNT_SETTINGS_PATH, {
            replace: true,
            state: { appleLinked: outcome.linkedEmail },
          })
          return
        }

        if ('secondFactorTicket' in outcome) {
          navigate('/login', {
            replace: true,
            state: { appleSecondFactorTicket: outcome.secondFactorTicket },
          })
          return
        }

        // No console account uses it: the login page asks whether to make
        // one, and nothing is made until the answer is yes.
        if ('newAccountTicket' in outcome) {
          navigate('/login', {
            replace: true,
            state: {
              newAccount: {
                ticket: outcome.newAccountTicket,
                via: 'apple',
                email: outcome.email,
              },
            },
          })
          return
        }

        // The same as any sign-in the backend answered with a session: the
        // invitation dealt with, then the console or onboarding.
        await teslaSignInComplete(
          outcome.token,
          outcome.invitation,
          outcome.invitation !== null,
        )
      } catch (failure) {
        await refuse(
          failure instanceof AppleTripError ? failure.message : DID_NOT_FINISH,
        )
      }
    })()
  }, [search, navigate, teslaSignInComplete, nothingToFinish])

  if (nothingToFinish) {
    return (
      <Page title="Apple sign in">
        <Grid
          container
          justifyContent="center"
          alignContent="center"
          style={{ minHeight: '100vh' }}
        >
          <Grid item xs={11} md={4}>
            <Stack spacing={2} alignItems="flex-start">
              <Typography variant="h2">Nothing to finish here</Typography>
              <Typography variant="body2" color="text.secondary">
                This page finishes an Apple sign in, and Apple did not send one.
                Start again from the sign in page.
              </Typography>
              <Button component={RouterLink} to="/login" variant="outlined">
                Go to sign in
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Page>
    )
  }

  return (
    <Page title="Login">
      <Grid
        container
        justifyContent="center"
        alignContent="center"
        style={{ minHeight: '100vh' }}
      >
        <Grid item xs={11} md={3}>
          <AnimatedLogo />
        </Grid>
      </Grid>
    </Page>
  )
}

export default AppleOauthPage
